import { useCallback, useState } from "react";

import { Box } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { generatePath, useNavigate } from "react-router";

import { PageName } from "analytics/events";
import { routes } from "config/routes";
import { StockChangeOverview } from "flows/Inventory/shared/components/StockChangeOverview";
import { TaskCheckHeader } from "flows/Inventory/shared/components/TaskCheckHeader";
import { useGetProductStockQuery } from "flows/Inventory/shared/queries/inventoryEntry/inventoryEntry.generated";
import { AskForRestockingButton } from "flows/Picking/components/AskForRestockingButton/AskForRestockingButton";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { transformChunks } from "shared/components/TextWrapper";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Button } from "ui/Button/Button";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { VerticalLayout } from "ui/VerticalLayout/VerticalLayout";

import { usePublicRestockingList } from "../../RestockingList/services/restockingService/service";
import { useRollingInventoryCheckMutation } from "../hooks/useRollingInventoryCheckMutation";
import { useRollingInventoryCheckService } from "../hooks/useRollingInventoryCheckService";

const ASK_FOR_RESTOCKING_ERROR_TOAST_ID = "ask_for_restocking_error_toast_for_sku";

export function RollingInventoryCheckUpdate() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();

  const context = useRollingInventoryCheckService();
  const { origin, sku, declaredQuantity, shelfLetter, shelfNumber, checkId } = context;

  const { addItemToPublicRestockingList } = usePublicRestockingList();

  const [totalNewStock, setTotalNewStock] = useState<number>(declaredQuantity);
  const [quantityExpired, setQuantityExpired] = useState<number>(0);
  const [quantityDamaged, setQuantityDamaged] = useState<number>(0);
  const [quantitytooGoodToGo, setQuantityTooGoodToGo] = useState<number>(0);
  const [isRestockingButtonDisabled, setIsRestockingButtonDisabled] = useState<boolean>(false);

  const { data, loading } = useGetProductStockQuery({
    fetchPolicy: "network-only",
    variables: { sku: sku!, isAlwaysInbound: true },
    skip: !sku,
  });

  const totalStock = data?.getProduct.inventoryEntry.stock?.shelf ?? 0;

  const { declareStock, isUpdating } = useRollingInventoryCheckMutation({ context });

  // Derive button states from totalNewStock
  const isCTADisabled = totalNewStock < 0;
  const isIncreaseBtnDisabled = totalNewStock === 0;

  const newStockHandler = useCallback((newStockCount: number) => {
    setTotalNewStock(newStockCount);
  }, []);

  const handleCompleteTask = useCallback(() => {
    declareStock({
      totalNewStock,
      quantityDamaged,
      quantityExpired,
      quantityTooGoodToGo: quantitytooGoodToGo,
      expectedQuantity: totalStock ?? 0,
    });
  }, [
    declareStock,
    totalNewStock,
    quantityDamaged,
    quantityExpired,
    quantitytooGoodToGo,
    totalStock,
  ]);

  const handleAskForRestocking = async () => {
    if (sku) {
      try {
        await addItemToPublicRestockingList(sku);
        setIsRestockingButtonDisabled(true);
      } catch (e: any) {
        showToastUI({
          id: `${ASK_FOR_RESTOCKING_ERROR_TOAST_ID}_${sku}`,
          title: "components.picking.restocking.ask-for-restocking-error-title",
        });
      }
      sendSegmentTrackEvent("click", {
        screen_name: PageName.INVENTORY_ROLLING_INVENTORY_CHECK_LIST_PAGE,
        component_name: "ask_for_restock",
        component_value: sku,
        component_content: checkId ?? "",
      });
    }
  };

  return (
    <Page isFull isCentered isBgGrey>
      <SpinnerModal isOpen={loading} />
      <TaskCheckHeader
        headerMessageId="pages.inventory.rolling-inventory-checks.title"
        onBack={() => {
          if (!checkId) return;
          navigate(generatePath(routes.inventory.rollingInventoryChecks.quantity, { checkId }));
        }}
        origin={origin}
        shelfLetter={shelfLetter}
        shelfNumber={shelfNumber}
      />
      <VerticalLayout flexGrow={1} alignItems="space-between" overflowY="scroll">
        <VerticalLayout p="s200" flexGrow={1} gap="s100">
          <StockChangeOverview
            counted={declaredQuantity || 0}
            expectedStock={totalStock}
            expiredCount={quantityExpired || 0}
            damagedCount={quantityDamaged || 0}
            tooGoodToGoCount={quantitytooGoodToGo || 0}
            newStockCallback={newStockHandler}
          />
          <VerticalLayout bg="white" pt="s50" px="s200" pb="s150" borderRadius="sm" gap="s50">
            <IncreaseDecrease
              mb="s200"
              label={
                <FormattedMessage
                  id="components.increasedecrease.how_many_are_expired"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setQuantityExpired}
              count={quantityExpired || 0}
              isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            />
            <IncreaseDecrease
              mb="s200"
              label={
                <FormattedMessage
                  id="components.increasedecrease.how_many_are_damaged"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setQuantityDamaged}
              count={quantityDamaged || 0}
              isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            />
            <IncreaseDecrease
              label={
                <FormattedMessage
                  id="components.increasedecrease.too_good_to_go"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setQuantityTooGoodToGo}
              count={quantitytooGoodToGo || 0}
              isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            />
          </VerticalLayout>
          <Box paddingTop="s150">
            <AskForRestockingButton
              disabled={isRestockingButtonDisabled}
              onAskForRestocking={handleAskForRestocking}
            />
          </Box>
        </VerticalLayout>

        <Box bg="white" p="s200" borderTop="solid 1px" borderTopColor="grey.200">
          <Button
            size="lg"
            isFullWidth
            onClick={handleCompleteTask}
            disabled={isCTADisabled || isUpdating}
            isLoading={isUpdating}
            data-testid="submit-update-button"
          >
            {intl.formatMessage({ id: "components.stock-change.complete-task-button" })}
          </Button>
        </Box>
      </VerticalLayout>
    </Page>
  );
}
